import { isProviderVisibleAtLocation } from "@/components/provider-select/provider-location-visibility";

import { CalendarDay, clinicClockFor, daysBetween } from "@/lib/clinic-clock";

import { PublicProviderSummary } from "@/types/provider";
import { AvailabilityRequestAssignment } from "@/types/self-schedule";
import { PublicServiceDetail } from "@/types/service";

import { FIRST_AVAILABLE_PROVIDER_ID } from "@/stores/self-schedule-store";

// A normal weekly schedule never leaves a gap this long.
const QUIET_DAYS = 7;

export type ProvidersBack = CalendarDay | "indefinitely";

const earlier = (a: string, b: string) => (new Date(a) < new Date(b) ? a : b);

/** The earliest of these next shifts among `providerIds`, or among everyone. */
function earliestShift(
  nextShiftStarts: Record<string, string>,
  providerIds?: string[],
): string | null {
  const starts = Object.entries(nextShiftStarts)
    .filter(([providerId]) => !providerIds || providerIds.includes(providerId))
    .map(([, start]) => start);
  return starts.length ? starts.reduce(earlier) : null;
}

/** When someone is back here for every selected service: the latest of each service's earliest shift. */
export function selectionShiftStart({
  nextShiftStarts,
  assignments,
  services,
  providers,
  locationId,
  pickedProviderId,
}: {
  nextShiftStarts: Record<string, string>;
  assignments: AvailabilityRequestAssignment[];
  services: PublicServiceDetail[];
  providers: PublicProviderSummary[];
  locationId: string;
  /** A provider chosen before any service, e.g. from a provider booking link. */
  pickedProviderId?: string;
}): string | null {
  const serviceById = new Map(services.map((service) => [service.id, service]));
  // A pick hidden here falls back to First available once the location is chosen.
  const shownHere = (providerId: string) =>
    isProviderVisibleAtLocation(
      providers.find(({ id }) => id === providerId)?.visibleLocationIds,
      locationId,
    );
  if (
    !assignments.length &&
    pickedProviderId &&
    pickedProviderId !== FIRST_AVAILABLE_PROVIDER_ID &&
    shownHere(pickedProviderId)
  )
    return earliestShift(nextShiftStarts, [pickedProviderId]);
  let latest: string | null = null;
  for (const { serviceId, providerId } of assignments) {
    const service = serviceById.get(serviceId);
    const providerIds =
      providerId &&
      providerId !== FIRST_AVAILABLE_PROVIDER_ID &&
      shownHere(providerId)
        ? [providerId]
        : (service?.effectiveProviderIds ?? service?.providerIds);
    // Add-ons hidden from the menu aren't on the front page; their parent decides.
    if (!providerIds) continue;
    const start = earliestShift(nextShiftStarts, providerIds);
    if (!start) return null;
    if (!latest || new Date(start) > new Date(latest)) latest = start;
  }
  return latest ?? earliestShift(nextShiftStarts);
}

/** The day providers return after a gap over a week; "indefinitely" when nobody has a shift. */
export function noProvidersUntil(
  shiftStart: string | null,
  timeZone: string,
): ProvidersBack | null {
  if (!shiftStart) return "indefinitely";
  const clock = clinicClockFor(timeZone);
  const firstDay = clock.dayOf(shiftStart);
  return daysBetween(clock.today(), firstDay) > QUIET_DAYS ? firstDay : null;
}
